import type { Region } from '~~/core/entities/Region';
import type { RegionReport } from '~~/core/ports/repositories';

import { getNormalizedRegionSlug } from '~~/utils/regionPath';

export type FeaturedRegionTagAvailability = Pick<
	Region,
	'topPropertyTypes' | 'typologies'
>;

type FeaturedRegionTagKeyInput = Pick<Region, 'name' | 'city' | 'state'>;

function getLocationKey({
	name,
	city,
	state
}: FeaturedRegionTagKeyInput): string {
	return [name, city, state]
		.map(value => getNormalizedRegionSlug(value))
		.join('|');
}

function getReportLocationKey(report: RegionReport): string {
	return getLocationKey({
		name: report.region,
		city: report.city,
		state: report.state
	});
}

function getRegionNameKey(regionName: string): string {
	return getNormalizedRegionSlug(regionName);
}

function getReportTagAvailability(
	report: RegionReport
): FeaturedRegionTagAvailability {
	return {
		topPropertyTypes: report.topPropertyTypes || [],
		typologies: report.typologies || []
	};
}

export function getFeaturedRegionTagKey(
	region: FeaturedRegionTagKeyInput
): string {
	return getLocationKey(region);
}

export function mapRegionReportsToFeaturedRegionTagAvailability(
	regions: Region[],
	reports: RegionReport[]
): Record<string, FeaturedRegionTagAvailability> {
	const reportByLocationKey = new Map(
		reports.map(report => [getReportLocationKey(report), report])
	);
	const reportByRegionNameKey = new Map(
		reports.map(report => [getRegionNameKey(report.region), report])
	);

	return regions.reduce<Record<string, FeaturedRegionTagAvailability>>(
		(availabilityByKey, region) => {
			const report =
				reportByLocationKey.get(getFeaturedRegionTagKey(region)) ||
				reportByRegionNameKey.get(getRegionNameKey(region.name));

			if (!report) return availabilityByKey;

			availabilityByKey[getFeaturedRegionTagKey(region)] =
				getReportTagAvailability(report);

			return availabilityByKey;
		},
		{}
	);
}
